import './SubmitRatingModal.scss'

import MicroModal from 'micromodal'
import {
  addChildClass,
  appendElement,
  removeChildClass,
  setChildContent,
  toggleChildAttribute,
  toggleChildClass
} from '../../utils/dom'
import {
  InternalComponentAPI,
  InternalPluginProps,
  UpdatedRatings
} from '../../utils/types'
import { addEventListener } from '../../utils/events'
import { withClass } from '../../utils/selectors'
import StarsInput from '../StarsInput'
import { StarsInputAPI } from '../StarsInput/StarsInput'

const CLOSE_MODAL_AFTER_SUCCESS_TIMEOUT = 1_500
const RATING_INPUT_CLASS = 'submit-rating-modal__rating-input'
const FEEDBACK_INPUT_CLASS = 'submit-rating-modal__feedback-input'
const FEEDBACK_INPUT_FOCUSED_CLASS = 'feedback-input-focused'
const SUBMIT_RATING_CLASS = 'submit-rating-modal__submit-btn'
const MODAL_OVERLAY_CLASS = 'submit-rating-modal__overlay'
const MODAL_CLOSE_BTN = 'submit-rating-modal__close'
const MODAL_CONTAINER_CLASS = 'submit-rating-modal__container'
const MODAL_BODY_CLASS = 'submit-rating-modal__body'
const MODAL_ERROR_CONTAINER_CLASS = 'submit-rating-modal__error'
const MODAL_ERROR_MESSAGE_CLASS = 'submit-rating-modal__error-message'

type SubmitRatingModalProps = InternalPluginProps & {
  modalParent: Element
}

/**
 * Submit rating modal component API
 */
export type SubmitRatingModalAPI = InternalComponentAPI & {
  show(): void
  hide(): void
}

/**
 * Submit rating modal component
 * @param modalParent Parent element where the modal should be appended
 */
const SubmitRatingModal = ({
  modalParent,
  ...props
}: SubmitRatingModalProps): SubmitRatingModalAPI => {
  let previousRating = props.currentUserRating?.rating
  let modal: Element
  let starInput: StarsInputAPI

  /**
   * Show error message
   */
  const showErrorMessage = (message?: string) => {
    addChildClass({
      parent: modal,
      selector: withClass(MODAL_ERROR_CONTAINER_CLASS),
      className: 'visible'
    })
    setChildContent({
      parent: modal,
      selector: withClass(MODAL_ERROR_MESSAGE_CLASS),
      html: message || props.translations.submitError
    })
  }

  /**
   * Show success messsage
   */
  const showSuccessMessage = () => {
    setChildContent({
      parent: modal,
      selector: withClass(MODAL_BODY_CLASS),
      html: submitRatingSuccess(props)
    })
    setTimeout(() => {
      MicroModal.close(submitRatingModalId(props.pluginId))
    }, CLOSE_MODAL_AFTER_SUCCESS_TIMEOUT)
  }

  /**
   * Hide the error message
   */
  const hideErrorMessage = () => {
    removeChildClass({
      parent: modal,
      selector: withClass(MODAL_ERROR_CONTAINER_CLASS),
      className: 'visible'
    })
  }

  /**
   * Logic to decide if the feedback input should be visible or not
   */
  const isFeedbackInputVisible = (rating: number): boolean => {
    if (rating === 5) {
      return false
    }
    if (previousRating && rating > previousRating) {
      return false
    }
    return true
  }

  /**
   * When rating changes, clear message and toggle feedback input
   */
  const onRatingChange = (rating: number) => {
    hideErrorMessage()
    toggleChildClass({
      parent: modal,
      selector: withClass(FEEDBACK_INPUT_CLASS),
      className: 'is-hidden',
      classPresent: !isFeedbackInputVisible(rating)
    })
  }

  /**
   * Put the modal on loading state while it is waitng for BE response
   */
  const toggleLoadingState = (loading: boolean) => {
    const btnLabel = loading
      ? props.translations.submitLoading
      : props.translations.submitRating
    starInput?.setDisabled(loading)
    setChildContent({
      parent: modal,
      selector: withClass(SUBMIT_RATING_CLASS),
      html: btnLabel
    })
    toggleChildAttribute({
      parent: modal,
      selector: withClass(SUBMIT_RATING_CLASS),
      attributeName: 'disabled',
      attributePresent: loading
    })
    toggleChildAttribute({
      parent: modal,
      selector: withClass(FEEDBACK_INPUT_CLASS),
      attributeName: 'disabled',
      attributePresent: loading
    })
    toggleChildAttribute({
      parent: modal,
      selector: withClass(MODAL_CLOSE_BTN),
      attributeName: 'disabled',
      attributePresent: loading
    })
    toggleChildAttribute({
      parent: modal,
      selector: withClass(MODAL_OVERLAY_CLASS),
      attributeName: 'data-micromodal-close',
      attributePresent: !loading
    })
    toggleChildAttribute({
      parent: modal,
      selector: withClass(MODAL_CLOSE_BTN),
      attributeName: 'data-micromodal-close',
      attributePresent: !loading
    })
  }

  /**
   * Read user input and execute submit rating callback
   */
  const submitRating = async () => {
    const rating = starInput.getRating()
    if (!rating) {
      return showErrorMessage(props.translations.invalidRatingError)
    }

    let feedback = ''
    if (isFeedbackInputVisible(rating)) {
      const feedbackInputEl = modal.querySelector(
        withClass(FEEDBACK_INPUT_CLASS)
      ) as HTMLTextAreaElement
      feedback = feedbackInputEl.value.trim()
    }

    hideErrorMessage()
    toggleLoadingState(true)

    props.onSubmitRating({
      itemId: props.itemId,
      rating,
      feedback,
      success: (updatedRatings: UpdatedRatings) => {
        props.internalAPI.updateRatings(updatedRatings)
        toggleLoadingState(false)
        showSuccessMessage()
      },
      error: message => {
        showErrorMessage(message)
        toggleLoadingState(false)
      }
    })
  }

  /**
   * Destroy component
   */
  const destroy = () => {
    starInput.destroy()
    modal.innerHTML = ''
    modal.parentNode?.removeChild(modal)
  }

  /**
   * Init component
   */
  const init = () => {
    modal = appendElement({
      parent: modalParent,
      html: submitRatingModal(props)
    }) as HTMLElement

    starInput = StarsInput({
      ...props,
      target: modal.querySelector(withClass(RATING_INPUT_CLASS))!,
      onRatingChange
    })

    addEventListener({
      target: modal,
      eventName: 'click',
      selector: withClass(SUBMIT_RATING_CLASS),
      handler: submitRating
    })

    /*
      // TODO: Removing header hiding temporarily (#44)

      const toggleInputFocusClass = (isFocused: boolean) => () => {
        // `setTimeout` is required here because when feedback textarea has focus and submit button is clicked:
        // 1) blur event will be triggered on mousedown
        // 2) header will be shown and submit button will be pushed down
        // 3) mouseup will be fired, but since the mouse is not on top of the button anymore, click won't be fired
        setTimeout(() =>
          toggleChildClass({
            parent: modal,
            selector: withClass(MODAL_CONTAINER_CLASS),
            className: FEEDBACK_INPUT_FOCUSED_CLASS,
            classPresent: isFocused
          })
        )
      }

      const feedbackInput = modal.querySelector(withClass(FEEDBACK_INPUT_CLASS))
      feedbackInput?.addEventListener('focus', toggleInputFocusClass(true))
      feedbackInput?.addEventListener('blur', toggleInputFocusClass(false))
    */
  }

  /**
   * Reset component state and UI
   */
  const reset = () => {
    destroy()
    init()
  }

  //
  // Initialize component
  //
  init()

  //
  // Public API
  //
  return {
    destroy,
    show: () => {
      MicroModal.show(submitRatingModalId(props.pluginId), {
        onClose: () => reset(),
        disableScroll: true
      })
    },
    hide: () => MicroModal.close(submitRatingModalId(props.pluginId)),
    updateRatings: (updatedRatings: UpdatedRatings) => {
      previousRating = updatedRatings.currentUserRating.rating
    }
  }
}

export default SubmitRatingModal

//
// Templates
//

const submitRatingModalId = (id: string) => `submit-rating-modal-${id}`

const submitRatingModal = (props: InternalPluginProps) => `
  <div
    aria-hidden="true"
    class="submit-rating-modal"
    id="${submitRatingModalId(props.pluginId)}"
  >
    <div class="${MODAL_OVERLAY_CLASS}" tabindex="-1" data-micromodal-close>
      <div
        class="${MODAL_CONTAINER_CLASS}"
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-1-title"
      >
        <header
          class="submit-rating-modal__header"
          style="background-image:url(${props.recipeImage});"
        >
          ${submitRatingModalCloseButton()}
        </header>
        <div class="${MODAL_BODY_CLASS}">
          ${submitRatingModalContent(props)}

          <footer class="submit-rating-modal__footer">
            ${submitRatingError(props)}
            ${submitRatingButton(props)}
          </footer>
        </div>
      </div>
    </div>
  </div>
`

const submitRatingModalCloseButton = () => `
  <button
    class="${MODAL_CLOSE_BTN}"
    aria-label="Close submit ratings dialog"
    data-micromodal-close
  ></button>
`

const submitRatingButton = ({ translations }: InternalPluginProps) => `
  <button class="submit-rating-modal__btn submit-rating-modal__btn-primary ${SUBMIT_RATING_CLASS}">
    ${translations.submitRating}
  </button>
`

const submitRatingModalContent = (props: InternalPluginProps) => `
  <main class="submit-rating-modal__content" id="modal-1-content">
    <div class="submit-rating-modal__subheader">
      <h2 class="submit-rating-modal__title" id="modal-1-title">${props.recipeTitle}</h2>
      <p class="submit-rating-modal__paragraph">
      ${props.translations.question}
      </p>
    </div>
    <div class="${RATING_INPUT_CLASS}"></div>
    <textarea
      placeholder="${props.translations.giveFeedback}"
      class="${FEEDBACK_INPUT_CLASS} is-hidden"
      autofocus
    ></textarea>
  </main>
`

const submitRatingError = ({ translations }: InternalPluginProps) => `
  <div class="${MODAL_ERROR_CONTAINER_CLASS}">
    <span class="submit-rating-modal__warning-icon"></span>
    <p class="${MODAL_ERROR_MESSAGE_CLASS}">
      ${translations.submitError}
    </p>
  </div>
`

const submitRatingSuccess = ({ translations }: InternalPluginProps) => `
  <div class="submit-rating-modal__success">
    <span class="submit-rating-modal__success-icon"></span>
    <p class="submit-rating-modal__error-message">
      ${translations.submitThanksForRating}
    </p>
  </div>
`
